import { Show } from "shared/components";
import {
  type ColDef,
  type SortChangedEvent,
  type ICellRendererParams,
  type GridReadyEvent,
  type GridApi,
  type GetRowIdParams,
  type IsFullWidthRowParams,
  type CellClickedEvent
} from "ag-grid-community";
import { AssigneeSelect } from "../AssigneeSelect";

import { useLocation, useNavigate } from "react-router-dom";
import { ApplicantStatus, ApplicationStatus } from "shared/types";
import {
  Dispatch,
  lazy,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { Application } from "types/application";

import { useAuthContext } from "providers/Auth";
import { RuleBaseGraph } from "shared/app/RuleBaseGraph";
import { ScoreGraphSize } from "views/applications-id/components/ApplicationScoreCard/constants";
import {
  ApplicationLike,
  getApplicationUIStatus
} from "utils/get-application-ui-status";
import { Loader } from "shared/components/Loader";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ScreeningStatusBadge } from "../ScreeningStatusBadge";
import { ActiveTab, newOrder } from "./constants";
import {
  getApplicantNames,
  getCombinedNamesAndEmails,
  getInviteesEmails
} from "./helpers";
import { useLayout } from "providers/Layout/hooks";
import {
  CashbackIcon,
  File02Icon,
  PathfinderDivideIcon,
  PauseIcon,
  PlayIcon,
  Search01Icon,
  UnavailableIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import {
  placeOnHoldApplication,
  resumeApplication
} from "shared/app/OnHoldResume/api";
import { useQueryClient } from "@tanstack/react-query";
import { ApplicationActions } from "shared/utils/application-actions";
import { DateTableCell } from "shared/components/DateTableCell";
import { DateTimeTableCell } from "shared/components/DateTimeTableCell";
import { camelToSentence, capitalize } from "shared/utils";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { Archive, ArrowRight } from "untitledui-js-base";
import { Checkbox, CheckboxSize } from "shared/components";
import {
  AgGrid,
  DEFAULT_AG_GRID_VIEWPORT_OFFSET
} from "shared/components/AgGrid";

const ScoreGraph = lazy(
  () =>
    import("views/applications-id/components/ApplicationScoreCard/ScoreGraph")
);

type ApplicationWithUIStatus = Application & {
  ui_status?: ApplicationStatus;
  isGroupHeader?: boolean;
  groupValue?: string;
};

const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
};

export const ApplicationsDataGridTable = ({
  groupBy,
  applications,
  onInviteClick,
  activeTab,
  hasSearchParams,
  isLoading,
  setSelectedRentersRow,
  selectedRentersRow,
  onCancelClick,
  onUncancelClick,
  onMoveClick,
  onRefundClick,
  onSplitClick,
  sorting,
  setSorting,
  sortableKeys,
  columnsVisibility,
  invalidateQueries,
  pagination,
  pagesCount = 1,
  perPageRows,
  viewPortHeightOffset
}: {
  groupBy?: string;
  applications: Application[];
  onInviteClick?: () => void;
  activeTab?: ActiveTab;
  isPending?: boolean;
  isCanceled?: boolean;
  hasSearchParams?: boolean;
  setSelectedRentersRow: Dispatch<SetStateAction<Record<string, boolean>>>;
  selectedRentersRow: Record<string, boolean>;
  onCancelClick?: (id: string) => void;
  onUncancelClick?: (id: string) => void;
  onMoveClick?: (id: string) => void;
  onRefundClick?: (id: string) => void;
  onSplitClick?: (id: string) => void;
  isLoading?: boolean;
  sorting: { id: string; desc: boolean }[];
  setSorting: Dispatch<SetStateAction<{ id: string; desc: boolean }[]>>;
  sortableKeys: string[];
  invalidateQueries?: unknown[][];
  columnsVisibility: { id: string; checked: boolean; disabled: boolean }[];
  pagination?: React.ReactNode;
  pagesCount?: number;
  perPageRows: number;
  viewPortHeightOffset?: number;
}) => {
  const { Unit, unit, Property } = useUnitsPropertiesDictionary();
  const { pathname } = useLocation();
  const { permissions } = useAuthContext();
  const { sectionHeight } = useLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const gridApiRef = useRef<GridApi | null>(null);

  const [selectedRows, setSelectedRows] = useState<Set<string>>(
    new Set(
      Object.keys(selectedRentersRow).filter((k) => selectedRentersRow[k])
    )
  );

  const tableApplications = useMemo<ApplicationWithUIStatus[]>(() => {
    const appsWithStatus = applications.map((application) => ({
      ...application,
      ui_status: getApplicationUIStatus(
        application as unknown as ApplicationLike
      )
    }));

    if (!groupBy || groupBy === "") return appsWithStatus;

    const groupByMap: Record<string, string> = {
      unit: "unit.title",
      status: "ui_status",
      screening_status: "screening_status"
    };

    const groupByKey = groupByMap[groupBy] || groupBy;
    const grouped = new Map<string, ApplicationWithUIStatus[]>();

    appsWithStatus.forEach((app) => {
      const groupValue = String(getNestedValue(app, groupByKey) || "");
      if (!grouped.has(groupValue)) grouped.set(groupValue, []);
      grouped.get(groupValue)!.push(app);
    });

    const result: ApplicationWithUIStatus[] = [];
    grouped.forEach((apps, groupValue) => {
      result.push({
        ...apps[0],
        id: `group-${groupValue}` as any,
        isGroupHeader: true,
        groupValue
      });
      result.push(...apps);
    });

    return result;
  }, [applications, groupBy]);

  const currentTab = {
    isAll: activeTab === ActiveTab.All,
    isNotSubmitted: activeTab === ActiveTab.NotSubmitted,
    isPartiallySubmitted: activeTab === ActiveTab.PartiallySubmitted,
    isSubmitted: activeTab === ActiveTab.Submitted,
    isApproved: activeTab === ActiveTab.Approved,
    isConditionallyApproved: activeTab === ActiveTab.ApprovedConditionally,
    isDeclined: activeTab === ActiveTab.Declined,
    isCanceled: activeTab === ActiveTab.Canceled,
    isOnHold: activeTab === ActiveTab.OnHold
  };

  const sortedColumnsVisibility = [...columnsVisibility].sort((a, b) => {
    const indexA = newOrder.indexOf(a.id);
    const indexB = newOrder.indexOf(b.id);
    return indexA - indexB;
  });

  const onResumeHoldSideEffect = () => {
    invalidateQueries?.forEach((queryKey) => {
      queryClient.refetchQueries({
        queryKey
      });
    });
  };

  const toggleRowSelection = (id: string) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);

      const newState: Record<string, boolean> = {};
      newSet.forEach((rowId) => {
        newState[rowId] = true;
      });
      setSelectedRentersRow(newState);

      return newSet;
    });
  };

  const columns = useMemo<ColDef<ApplicationWithUIStatus>[]>(() => {
    const cols: ColDef<ApplicationWithUIStatus>[] = [];

    // Checkbox column
    cols.push({
      headerName: "",
      colId: "checkbox",
      field: "id" as any,
      pinned: "left",
      width: 64,
      resizable: false,
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<ApplicationWithUIStatus>) => {
        const row = params.data;
        if (!row) return null;

        return (
          <div
            className="flex items-center justify-center w-full h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip placement="top-end">
              <TooltipTrigger>
                <Checkbox
                  wrapperClassName="!w-auto !flex-none !p-3 justify-center hover:bg-alpha-40 rounded-full"
                  size={CheckboxSize.SM}
                  checked={selectedRows.has(String(row.id))}
                  disabled={row.status === ApplicationStatus.OnHold}
                  onChange={() => toggleRowSelection(String(row.id))}
                />
                <Show if={row.status === ApplicationStatus.OnHold}>
                  <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white">
                    Resume this application before selecting it for merging
                  </TooltipContent>
                </Show>
              </TooltipTrigger>
            </Tooltip>
          </div>
        );
      }
    });

    // Dynamic columns based on visibility
    sortedColumnsVisibility.forEach((col) => {
      if (!col.checked || col.disabled) return;

      if (col.id === "criteria")
        cols.push({
          headerName: "Criteria",
          field: "applicant_score" as any,
          resizable: false,
          width: 90,
          sortable: false,
          cellRenderer: (params: any) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            const templateCriteriaSettings =
              row.verification_template?.screening_criteria;

            const hasValue =
              [
                ApplicationStatus.Submitted,
                ApplicationStatus.Approved,
                ApplicationStatus.Rejected,
                ApplicationStatus.ConditionalApproved,
                ApplicationStatus.Canceled
              ].includes(row.status) || row.submitted_at;

            if (
              !row.verification_template?.recommendation_method ||
              row.verification_template.recommendation_method === "none"
            )
              return null;

            if (
              row.verification_template.recommendation_method === "score_based"
            )
              return (
                <div className="overflow-hidden pl-2">
                  <Suspense fallback={<Loader />}>
                    <ScoreGraph
                      size={ScoreGraphSize.XS}
                      score={hasValue ? row.score : null}
                      declineRangeEnd={
                        templateCriteriaSettings?.decline_range_end
                      }
                      conditionalApprovalRangeEnd={
                        templateCriteriaSettings?.conditional_approval_range_end
                      }
                    />
                  </Suspense>
                </div>
              );

            return (
              <div className="overflow-hidden pl-6">
                <RuleBaseGraph
                  recommendation={hasValue ? row.recommendation : null}
                />
              </div>
            );
          }
        });

      if (col.id === "applicants")
        cols.push({
          headerName: "Applicants",
          field: "applicants" as any,
          width: 240,
          pinned: "left",
          resizable: true,
          sortable: sortableKeys.includes("applicants"),
          cellRenderer: (params: any) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            const { applicants, pending_magic_links } = row;

            const total_applicants =
              (applicants.length || 0) + (pending_magic_links?.length || 0);

            const applicantNames = getApplicantNames(applicants as any);
            const inviteesEmails = getInviteesEmails(pending_magic_links || []);

            const numApplicantsPrefix =
              total_applicants > 1 ? `(${total_applicants}) ` : "";
            return (
              <p className="truncate mr-2">
                {`${numApplicantsPrefix} ${getCombinedNamesAndEmails(applicantNames, inviteesEmails)}`}
              </p>
            );
          }
        });

      if (col.id === "property" && !pathname.includes("properties"))
        cols.push({
          headerName: Property,
          field: "property.address.address1" as any,
          width: 240,
          resizable: true,
          sortable: false,
          cellRenderer: (params: any) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <span className="block 2xl:max-w-none space-y-2">
                <span className="text-sm font-medium text-content-1000 truncate block">
                  {row.property.name}
                </span>
                <span className="text-xs text-content-400 truncate block">
                  {row.property.address.address1}
                </span>
              </span>
            );
          }
        });

      if (col.id === "property_group")
        cols.push({
          headerName: `${Property} group`,
          field: "property.property_group.name" as any,
          width: 140,
          minWidth: 100,
          resizable: true,
          sortable: false,
          cellRenderer: (params: any) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap 2xl:max-w-[140px] max-w-[80px]">
                {row.property.property_group?.name ?? "--"}
              </span>
            );
          }
        });

      if (col.id === "unit")
        cols.push({
          headerName: Unit,
          field: "unit.title" as any,
          width: 98,
          minWidth: 98,
          resizable: true,
          sortable: false,
          cellRenderer: (params: any) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            const isUnlisted = row.unit_delisted === true;

            return (
              <div className="flex items-center gap-1">
                {isUnlisted && (
                  <Tooltip placement="top">
                    <TooltipTrigger>
                      <div className="bg-danger-50 flex items-center justify-center p-1 rounded-2xl shrink-0">
                        <HugeiconsIcon
                          icon={UnavailableIcon}
                          size={12}
                          className="text-danger-500"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white max-w-[250px]">
                      Applications for {row.unit.title} are no longer allowed
                    </TooltipContent>
                  </Tooltip>
                )}
                <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-content-500">
                  {row.unit.title}
                </span>
              </div>
            );
          }
        });

      if (col.id === "submitted_at")
        cols.push({
          headerName: "Submitted",
          field: "submitted_at",
          width: 110,
          minWidth: 110,
          sortable: sortableKeys.includes("submitted_at"),
          cellRenderer: (params: any) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <DateTimeTableCell
                className="whitespace-nowrap"
                value={row.submitted_at}
              />
            );
          }
        });

      if (col.id === "desired_move_in_date")
        cols.push({
          headerName: "Move-in date",
          field: "desired_move_in_date",
          width: 140,
          minWidth: 140,
          sortable: sortableKeys.includes("desired_move_in_date"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data;
            if (!row) return null;

            return (
              <DateTableCell
                className="whitespace-nowrap"
                value={row.desired_move_in_date}
              />
            );
          }
        });

      if (col.id === "started_at")
        cols.push({
          headerName: "Started",
          field: "started_at",
          width: 110,
          minWidth: 110,
          sortable: sortableKeys.includes("started_at"),
          cellRenderer: (params: any) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <DateTimeTableCell
                className="whitespace-nowrap"
                value={row.started_at}
              />
            );
          }
        });

      if (col.id === "updated_at")
        cols.push({
          headerName: "Updated at",
          field: "updated_at",
          width: 135,
          minWidth: 135,
          sortable: sortableKeys.includes("updated_at"),
          cellRenderer: (params: any) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <DateTimeTableCell
                className="whitespace-nowrap"
                value={row.updated_at}
              />
            );
          }
        });

      if (col.id === "assigned_to")
        cols.push({
          headerName: "Assigned to",
          field: "assignee_ids" as any,
          width: 240,
          minWidth: 200,
          resizable: true,
          cellRenderer: (params: any) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            if (row.isGroupHeader) return null;

            return (
              <AssigneeSelect
                value={row.assignee_ids?.[0] ?? null}
                rowId={String(row.id)}
              />
            );
          }
        });
    });

    // Status column
    if (
      sortedColumnsVisibility.find((col) => col.id === "status" && col.checked)
    )
      cols.push({
        headerName: "Application status",
        field: "status",
        width: 170,
        minWidth: 130,
        resizable: true,
        sortable: sortableKeys.includes("status"),
        cellRenderer: (params: any) => {
          const row = params.data as ApplicationWithUIStatus | undefined;
          if (!row) return null;

          const { conditions, status, auto_approved, ui_status, applicants } =
            row;
          const applicationStatus = ui_status;

          const isAutoCanceled =
            status === ApplicationStatus.Canceled &&
            applicants.every(
              (applicant) =>
                applicant.application_status === ApplicantStatus.Canceled &&
                applicant.canceled_by_auto_cancel
            ) &&
            !applicants.some(
              (applicant) =>
                applicant.application_status === ApplicantStatus.Submitted
            );

          return (
            <div className="flex w-full">
              <ApplicationStatusBadge
                status={applicationStatus}
                conditions={conditions}
                type="application"
                isAutoCanceled={isAutoCanceled}
                autoApproved={auto_approved}
              />
            </div>
          );
        }
      });

    // Actions column
    const actionPerms = permissions;

    cols.push({
      headerName: "",
      field: "id" as any,
      colId: "actions",
      width: 60,
      minWidth: 60,
      pinned: "right",
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<ApplicationWithUIStatus>) => {
        const row = params.data;
        if (!row) return null;

        const id = String(row.id);
        const status = row.status as ApplicationStatus;
        const appActions = new ApplicationActions({
          status,
          permissions: actionPerms,
          applicants: row.applicants,
          application: row,
          unitLabel: unit,
          selectedForMerge: selectedRows.has(id)
        });

        const menuActions: {
          title: string | JSX.Element;
          onClick: () => void;
          disabled?: boolean;
          tooltip?: string;
        }[] = [
          {
            title: "Review",
            onClick: () => {
              navigate(`/applications/${id}`);
            }
          }
        ];

        // Resume / Place on hold
        const { resume, placeOnHold, move, split, uncancel, cancel, refund } =
          appActions;

        if (!resume.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <HugeiconsIcon
                  icon={PlayIcon}
                  size={20}
                  className="text-content-200 shrink-0"
                />
                Resume
              </span>
            ),
            onClick: () => {
              resumeApplication({
                applicationId: id,
                onSuccess: onResumeHoldSideEffect
              });
            }
          });
        else if (!placeOnHold.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <HugeiconsIcon
                  icon={PauseIcon}
                  size={20}
                  className="text-content-200 shrink-0"
                />
                Place on hold
              </span>
            ),
            disabled: placeOnHold.disabled,
            tooltip: placeOnHold.tooltip,
            onClick: () => {
              placeOnHoldApplication({
                applicationId: id,
                onSuccess: onResumeHoldSideEffect
              });
            }
          });

        // Move to another unit
        if (!move.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <ArrowRight size="18" className="stroke-content-200" />
                Move to another {unit}
              </span>
            ),
            tooltip: move.tooltip,
            disabled: move.disabled,
            onClick: () => onMoveClick?.(id)
          });

        // Split application
        if (!split.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <HugeiconsIcon
                  icon={PathfinderDivideIcon}
                  size={18}
                  className="text-content-200 shrink-0"
                />
                Split application
              </span>
            ),
            disabled: split.disabled,
            tooltip: split.tooltip,
            onClick: () => onSplitClick?.(id)
          });

        // Uncancel / Cancel
        if (!uncancel.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <Archive size="18" className="stroke-content-200" />
                Uncancel application
              </span>
            ),
            disabled: uncancel.disabled,
            tooltip: uncancel.tooltip,
            onClick: () => onUncancelClick?.(id)
          });
        else if (!cancel.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <Archive size="18" className="stroke-content-200" />
                Cancel application
              </span>
            ),
            tooltip: cancel.tooltip,
            disabled: cancel.disabled,
            onClick: () => onCancelClick?.(id)
          });

        // Refund application
        if (!refund.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <HugeiconsIcon
                  icon={CashbackIcon as IconSvgElement}
                  className="text-content-200"
                  size={18}
                />
                Refund application
              </span>
            ),
            disabled: refund.disabled,
            tooltip: refund.tooltip,
            onClick: () => onRefundClick?.(id)
          });

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <ContextMenuPopover
              placement="bottom-start"
              actions={menuActions}
            />
          </div>
        );
      }
    });

    // Screening status column (last, flex to fill remaining space)
    cols.push({
      headerName: "Screening status",
      field: "screening_status",
      width: 150,
      minWidth: 120,
      flex: 1,
      resizable: true,
      sortable: sortableKeys.includes("screening_status"),
      cellRenderer: (params: ICellRendererParams<ApplicationWithUIStatus>) => {
        const row = params.data;
        if (!row) return null;

        return <ScreeningStatusBadge status={row.screening_status} />;
      }
    });

    return cols;
  }, [
    sortedColumnsVisibility,
    pathname,
    Property,
    Unit,
    selectedRows,
    sortableKeys,
    groupBy
  ]);

  const onGridReady = useCallback(
    (params: GridReadyEvent<ApplicationWithUIStatus>) => {
      gridApiRef.current = params.api;
    },
    []
  );

  const onSortChanged = useCallback(
    (event: SortChangedEvent) => {
      const colState = event.api.getColumnState();
      const sortedCol = colState.find((c) => c.sort);
      if (sortedCol)
        setSorting([
          {
            id: sortedCol.colId,
            desc: sortedCol.sort === "desc"
          }
        ]);
      else setSorting([]);
    },
    [setSorting]
  );

  // Apply external sort state to ag-grid
  useEffect(() => {
    if (!gridApiRef.current) return;
    const columnState = sorting.map((s) => ({
      colId: s.id,
      sort: s.desc ? ("desc" as const) : ("asc" as const)
    }));
    gridApiRef.current.applyColumnState({
      state: columnState,
      defaultState: { sort: null }
    });
  }, [sorting]);

  const onCellClicked = useCallback(
    (event: CellClickedEvent<ApplicationWithUIStatus>) => {
      const row = event.data;
      if (!row || row.isGroupHeader) return;
      const colId = event.column.getColId();
      if (
        colId === "checkbox" ||
        colId === "actions" ||
        colId === "assignee_ids"
      )
        return;
      navigate(`/applications/${row.id}`);
    },
    [navigate]
  );

  const [emptyStateTitle, EmptyStateIcon] = useMemo<
    [string, IconSvgElement]
  >(() => {
    if (hasSearchParams) return ["No results found", Search01Icon];

    if (currentTab.isAll) return ["Applications will appear here", File02Icon];

    let prefix: string = activeTab || "";
    if (currentTab.isConditionallyApproved) prefix = "Conditionally approved";
    else if (currentTab.isOnHold) prefix = "On hold";
    else prefix = capitalize(camelToSentence(prefix).toLowerCase());

    const title = currentTab.isSubmitted
      ? "Submitted applications will appear here"
      : `${prefix || ""} applications will appear here`;

    const Icon = File02Icon;

    return [title, Icon];
  }, [currentTab, activeTab, hasSearchParams]);

  const getRowId = useCallback(
    (params: GetRowIdParams<ApplicationWithUIStatus>) => String(params.data.id),
    []
  );

  const isFullWidthRow = useCallback(
    (params: IsFullWidthRowParams<ApplicationWithUIStatus>) =>
      !!params.rowNode.data?.isGroupHeader,
    []
  );

  const fullWidthCellRenderer = useCallback(
    (params: ICellRendererParams<ApplicationWithUIStatus>) => {
      const row = params.data;
      if (!row?.groupValue) return null;

      if (groupBy === "status" || groupBy === "ui_status")
        return (
          <div className="flex items-center h-full px-4">
            <ApplicationStatusBadge
              type="application"
              status={row.groupValue as ApplicationStatus}
            />
          </div>
        );

      if (groupBy === "screening_status")
        return (
          <div className="flex items-center h-full px-4">
            <ScreeningStatusBadge status={row.groupValue} />
          </div>
        );

      return (
        <div className="flex items-center h-full px-4">
          <span className="text-content-400 text-sm font-medium">
            {row.groupValue}
          </span>
        </div>
      );
    },
    [groupBy]
  );

  return (
    <AgGrid<ApplicationWithUIStatus>
      columns={columns}
      rows={tableApplications}
      loading={isLoading}
      perPageRows={perPageRows}
      viewPortHeight={sectionHeight}
      viewPortHeightOffset={
        viewPortHeightOffset ?? DEFAULT_AG_GRID_VIEWPORT_OFFSET
      }
      getRowHeight={(params) => {
        if (params.data?.isGroupHeader) return 48;
        return 64;
      }}
      isFullWidthRow={isFullWidthRow}
      fullWidthCellRenderer={fullWidthCellRenderer}
      onGridReady={onGridReady}
      onSortChanged={onSortChanged}
      onCellClicked={onCellClicked}
      getRowId={getRowId}
      footer={pagination}
      showFooter={pagesCount > 1}
      noRowsFallback={
        <NoRowsFallback
          Icon={EmptyStateIcon}
          iconStrokeWidth={!hasSearchParams ? 1.5 : undefined}
          title={emptyStateTitle}
          text={
            hasSearchParams
              ? "We couldn't find any data matching your query"
              : null
          }
        />
      }
    />
  );
};
